import { Component, OnInit } from '@angular/core';
import { SharedService } from '../../../../services/shared/shared.service';
import { FormsModule, NgForm } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { CommonModule } from '@angular/common';
import { JobDto } from '../../../../models/job-dto';
import { FirestoreService } from '../../../../services/firestore/firestore.service';
import Swal from 'sweetalert2';
import { RouterLink } from '@angular/router';
import { JobService } from '../../../../services/job/job.service';
import { NotificationRequest } from '../../../../models/notification-request';
import { NotificationService } from '../../../../services/notification/notification.service';

@Component({
  selector: 'app-job-list',
  imports: [FormsModule, CommonModule, RouterLink],
  templateUrl: './job-list.component.html',
  styleUrls: ['./job-list.component.css']
})
export class JobListComponent implements OnInit {

  jobs: JobDto[] = [];
  currentJob: JobDto = {
    title: '',
    text: '',
    category: '',
    faqs: [],
    id: ''
  };
  editMode = false;
  selectedFile?: File;
  showSpinner = true;
  customCatigory = "";
  categories: string[] = [];
  selectedCategory: string = '';
  userIds: any;

  constructor(
    private sharedService: SharedService,
    private spinner: NgxSpinnerService,
    private firestoreService: FirestoreService,
    private jobService: JobService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.getAllJob();
    this.loadCategories();
    this.loadUserIds();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    }
  }

  addFaq() {
    this.currentJob.faqs = this.currentJob.faqs || [];
    this.currentJob.faqs.push({ question: '', answer: '' });
  }

  removeFaq(index: number) {
    this.currentJob.faqs?.splice(index, 1);
  }

  getAllJob() {
    this.firestoreService.getAll('jobs').subscribe({
      next: (data) => {
        this.jobs = data;
        this.showSpinner = false;
      },
      error: (error) => {
        this.showSpinner = false;
      }
    });
  }

  saveJob(form: NgForm) {
    this.spinner.show();
    if (form.invalid) return;

    if (this.customCatigory) {
      this.currentJob.category = this.customCatigory;
    }

    const saveOrUpdate = () => {
      if (this.editMode && this.currentJob.id) {
        // Update job
        this.firestoreService.update('jobs', this.currentJob.id, this.currentJob).subscribe({
          next: () => {
            this.spinner.hide();
            this.resetForm();
            this.getAllJob();
            this.sharedService.handleSuccess("Stelle erfolgreich aktualisiert");
          },
          error: (err) => {
            this.spinner.hide();
          }
        });
      } else {
        // Add new job
        this.firestoreService.add('jobs', this.currentJob).subscribe({
          next: (docRef) => {
            this.sharedService.handleSuccess("Stelle erfolgreich hinzugefügt");
            this.getAllJob();

            // ✅ Send notification to all users
            this.firestoreService.getUserIds().subscribe({
              next: userIds => {
                if (userIds && userIds.length > 0) {
                  const request = new NotificationRequest();
                  request.title = "Neue Stelle veröffentlicht";
                  request.body = `Eine neue Stelle "${this.currentJob.title}" wurde hinzugefügt. Schauen Sie sie sich an!`;
                  request.type = "NEW_JOB_POSTED";
                  request.userIds = userIds;
                  request.status = "UNREAD";

                  this.notificationService.saveNotification(request).subscribe({
                    next: () => this.resetForm()
                  });
                }
              },
              error: err => this.sharedService.handleError(err, undefined, "Fehler beim Abrufen der Benutzer-IDs.")
            });
          },
          error: (err) => {
            this.spinner.hide();
          }
        });
      }
    };

    // If a file is selected, upload it first and get the URL
    if (this.selectedFile) {
      const filePath = `job-images/${Date.now()}_${this.selectedFile.name}`;
      this.firestoreService.uploadFile(filePath, this.selectedFile).subscribe({
        next: (url) => {
          this.currentJob.image = url;
          saveOrUpdate();
        },
      });
    } else {
      saveOrUpdate();
    }
  }

  editJob(job: JobDto) {
    this.currentJob = { ...job, faqs: job.faqs || [] };
    this.editMode = true;
    this.selectedFile = undefined;
    const modal = new (window as any).bootstrap.Modal(document.getElementById('jobModal'));
    modal.show();
  }

  getJobById(id: string) {
    this.firestoreService.getById('jobs', id).subscribe({
      next: (job) => {
        this.currentJob = { ...job, faqs: job.faqs || [] };
      },
      error: (err) => {
        this.sharedService.handleError(err, "Fehler beim Laden der Stelle:");
      }
    });
  }

  deleteJob(id: string) {
    Swal.fire({
      title: 'Sind Sie sicher?',
      text: 'Diese Aktion löscht die Stelle dauerhaft.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ja, löschen!',
      cancelButtonText: 'Abbrechen'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.firestoreService.delete('jobs', id).subscribe({
          next: () => {
            this.spinner.hide();
            this.getAllJob();
            Swal.fire('Gelöscht!', 'Die Stelle wurde gelöscht.', 'success');
          },
          error: (err) => {
            this.sharedService.handleError(err, undefined, "Fehler beim Löschen der Stelle.")
          }
        });
      }
    });
  }

  resetForm() {
    this.currentJob = { title: '', text: '', category: '', faqs: [], id: "" };
    this.editMode = false;
    this.selectedFile = undefined;
  }

  private closeModal() {
    const modalEl = document.getElementById('jobModal');
    if (modalEl) {
      const modalInstance = (window as any).bootstrap.Modal.getInstance(modalEl);
      modalInstance?.hide();
    }
  }

  loadCategories() {
    this.jobService.getAllCategories().subscribe(categories => {
      this.categories = categories;
    });
  }

  filterByCategory() {
    if (this.selectedCategory) {
      this.jobService.getJobsByCategory(this.selectedCategory).subscribe(jobs => {
        this.jobs = jobs;
      });
    } else {
      // If "All Categories" selected, show all jobs
      this.getAllJob();
    }
  }

  private loadUserIds() {
    this.firestoreService.getAdminUserIds().subscribe({
      next: userIds => this.userIds = userIds,
    });
  }
}